// Prefetcher — download semua media di manifest ke IndexedDB sebelum dirender.
// Tujuan: konten siap diputar offline-first, no buffering saat transisi.
//
// Strategy:
//   - Cek tiap media di cache (by id). Skip kalau sudah ada.
//   - Download paralel dengan limit konkurensi (default 3) supaya tidak
//     overload WiFi lemah.
//   - Track progress: emit `onProgress(completed, total, currentName)`.
//   - Toleran error: kalau 1 media gagal, tetap lanjut yang lain (player
//     akan tampilkan placeholder untuk yang gagal).

import { hasCachedMedia, putCachedMedia } from "./media-cache.js";

interface PrefetchItem {
  id: string;
  url: string;
  mime_type: string | null;
  name: string;
}

export interface PrefetchProgress {
  total: number;
  completed: number;
  failed: number;
  currentName?: string;
  bytesLoaded: number;
}

export interface PrefetchResult {
  totalItems: number;
  cachedHits: number; // sudah ada di cache, tidak download
  freshDownloads: number;
  failed: string[]; // media IDs yang gagal
}

const CONCURRENCY = 3;

export async function prefetchMedia(
  items: PrefetchItem[],
  onProgress?: (p: PrefetchProgress) => void,
): Promise<PrefetchResult> {
  const result: PrefetchResult = {
    totalItems: items.length,
    cachedHits: 0,
    freshDownloads: 0,
    failed: [],
  };
  const progress: PrefetchProgress = {
    total: items.length,
    completed: 0,
    failed: 0,
    bytesLoaded: 0,
  };

  // Dedup by id (manifest playlist mungkin punya media yang sama 2x).
  const uniqueMap = new Map<string, PrefetchItem>();
  for (const it of items) {
    if (!uniqueMap.has(it.id)) uniqueMap.set(it.id, it);
  }
  const unique = Array.from(uniqueMap.values());
  progress.total = unique.length;
  result.totalItems = unique.length;

  // Process dengan worker pool.
  const queue = [...unique];
  const workers: Promise<void>[] = [];

  async function worker() {
    while (queue.length > 0) {
      const item = queue.shift();
      if (!item) break;
      progress.currentName = item.name;
      onProgress?.(progress);

      try {
        if (await hasCachedMedia(item.id)) {
          result.cachedHits++;
        } else {
          const res = await fetch(item.url, { cache: "no-store" });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          await putCachedMedia({
            id: item.id,
            url: item.url,
            mime_type: item.mime_type ?? blob.type ?? "application/octet-stream",
            blob,
            etag: res.headers.get("etag") ?? undefined,
          });
          result.freshDownloads++;
          progress.bytesLoaded += blob.size;
        }
      } catch (err) {
        console.warn(`[prefetch] gagal ${item.name}:`, err);
        result.failed.push(item.id);
        progress.failed++;
      } finally {
        progress.completed++;
        onProgress?.(progress);
      }
    }
  }

  for (let i = 0; i < Math.min(CONCURRENCY, unique.length); i++) {
    workers.push(worker());
  }
  await Promise.all(workers);

  return result;
}
